import { createContext, useContext, useState, useCallback, useRef } from 'react';
import StepUpModal from '../components/StepUpModal';

/**
 * Provides a `runWithStepUp(fn, actionLabel?)` helper that any descendant
 * can use to wrap a sensitive admin call. If the call returns
 * 401-STEPUP_REQUIRED, the modal opens, the admin re-authenticates, and
 * the call retries automatically.
 *
 * Usage:
 *   const { runWithStepUp } = useStepUpGate();
 *   try {
 *     await runWithStepUp(() => deleteUser(id), 'Delete user account');
 *   } catch (err) { ... }
 */
const StepUpContext = createContext(null);

export function StepUpProvider({ children }) {
  const [request, setRequest] = useState(null);
  const promiseRef = useRef(null);

  const open = useCallback((actionLabel) => {
    return new Promise((resolve, reject) => {
      promiseRef.current = { resolve, reject };
      setRequest({
        action: actionLabel,
        resolve: () => { promiseRef.current?.resolve?.(); promiseRef.current = null; },
        reject:  (e) => { promiseRef.current?.reject?.(e);  promiseRef.current = null; },
      });
    });
  }, []);

  const runWithStepUp = useCallback(async (fn, actionLabel) => {
    try {
      return await fn();
    } catch (err) {
      if (err?.code !== 'STEPUP_REQUIRED') throw err;
      // Use the action label the backend sent if the caller didn't supply one.
      await open(actionLabel || err.action || 'Sensitive action');
      // Retry once. If still 401-STEPUP_REQUIRED, surface that to the caller —
      // the modal will re-trigger on the next user action rather than looping.
      return await fn();
    }
  }, [open]);

  return (
    <StepUpContext.Provider value={{ runWithStepUp }}>
      {children}
      <StepUpModal request={request} onClose={() => setRequest(null)} />
    </StepUpContext.Provider>
  );
}

export function useStepUpGate() {
  const ctx = useContext(StepUpContext);
  if (!ctx) {
    // Allow component reuse outside the provider — calls just go straight
    // through and surface the STEPUP_REQUIRED error to the caller's own
    // try/catch. The Admin pages wrap themselves in the provider.
    return {
      runWithStepUp: async (fn) => fn(),
    };
  }
  return ctx;
}
